export const INVOICE_STATUSES = ['sent', 'paid', 'cancelled'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const INVOICE_STATUS_LABELS_HE: Record<InvoiceStatus, string> = {
  sent: 'ממתינה לתשלום',
  paid: 'שולמה',
  cancelled: 'בוטלה',
};

export const PAYMENT_METHODS = ['transfer', 'credit', 'cash', 'check', 'bit', 'other'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const PAYMENT_METHOD_LABELS_HE: Record<PaymentMethod, string> = {
  transfer: 'העברה בנקאית',
  credit: 'כרטיס אשראי',
  cash: 'מזומן',
  check: "צ'ק",
  bit: 'ביט',
  other: 'אחר',
};

export interface ProjectInvoice {
  id: string;
  project_id: string;
  invoice_number: string;
  status: InvoiceStatus;
  amount: number;
  currency: string;
  sent_at: string | null;
  paid_at: string | null;
  notes: string | null;
  created_at: string;
}

export interface ProjectPayment {
  id: string;
  project_id: string;
  invoice_id: string | null;
  amount: number;
  currency: string;
  paid_at: string;
  method: PaymentMethod;
  notes: string | null;
  created_at: string;
}

export interface InvoiceBalance {
  paid: number;
  due: number;
}

export interface FinanceSummary {
  value: number;
  /** Sum of invoices that are not cancelled. */
  invoiced: number;
  /** Every payment received, attached to an invoice or not (advances count). */
  received: number;
  /** Project value not yet invoiced. */
  toInvoice: number;
  /** Invoiced but not yet received. An advance larger than invoices makes this 0, not negative. */
  toCollect: number;
  /** Project value not yet received at all. */
  outstanding: number;
  byInvoice: Record<string, InvoiceBalance>;
}

function money(n: number): number {
  return Math.round(n * 100) / 100;
}

export function summarizeFinance(
  value: number,
  invoices: Pick<ProjectInvoice, 'id' | 'amount' | 'status'>[],
  payments: Pick<ProjectPayment, 'amount' | 'invoice_id'>[],
): FinanceSummary {
  const live = invoices.filter((i) => i.status !== 'cancelled');
  const invoiced = money(live.reduce((s, i) => s + i.amount, 0));
  const received = money(payments.reduce((s, p) => s + p.amount, 0));

  const byInvoice: Record<string, InvoiceBalance> = {};
  for (const inv of invoices) {
    const paid = money(
      payments.filter((p) => p.invoice_id === inv.id).reduce((s, p) => s + p.amount, 0),
    );
    byInvoice[inv.id] = {
      paid,
      due: inv.status === 'cancelled' ? 0 : money(Math.max(0, inv.amount - paid)),
    };
  }

  return {
    value: money(value),
    invoiced,
    received,
    toInvoice: money(Math.max(0, value - invoiced)),
    toCollect: money(Math.max(0, invoiced - received)),
    outstanding: money(Math.max(0, value - received)),
    byInvoice,
  };
}

export function formatILS(amount: number): string {
  return `₪ ${new Intl.NumberFormat('he-IL', { maximumFractionDigits: 2 }).format(amount)}`;
}
